import { API_PREFIX, paginationMetaDtoSchema, type HttpClient } from "@nasam/kernel";
import { z } from "zod";

import {
  derivePlaybackSegments,
  playbackAlertsOf,
  type TrackingAccount,
  type TrackingRepository,
} from "../domain";
import {
  countsDtoSchema,
  countsFromDto,
  parsePlaybackPoints,
  vehiclesFromDto,
} from "./trackingWire";

const TRACKING_PATH = `${API_PREFIX}/tracking`;

const accountSchema = z.object({ id: z.string(), nameEn: z.string(), nameAr: z.string() });
const accountsResponseSchema = z.union([
  z.array(accountSchema),
  z.object({ data: z.array(accountSchema) }),
]);

const vehiclesPageSchema = z.object({ data: z.array(z.unknown()), meta: paginationMetaDtoSchema });
const countsSchema = z.object({ data: countsDtoSchema });
const idsSchema = z.object({ data: z.array(z.string()) });
const addressSchema = z.object({
  data: z.object({
    addressEn: z.string().nullish(),
    addressAr: z.string().nullish(),
  }),
});

/** A blank address is none. */
const addressText = (value: string | null | undefined) => value?.trim() || null;

/** The query as request params: only what's set. */
function params(query: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ""),
  );
}

/** The tracking REST endpoints next to the live socket. */
export function createTrackingHttpRepository(http: HttpClient): TrackingRepository {
  return {
    async listAccounts(): Promise<readonly TrackingAccount[]> {
      const parsed = accountsResponseSchema.parse(await http.get(`${TRACKING_PATH}/accounts`));
      return Array.isArray(parsed) ? parsed : parsed.data;
    },
    async listVehicles(query) {
      const response = vehiclesPageSchema.parse(
        await http.get(`${TRACKING_PATH}/vehicles`, { params: params(query) }),
      );
      return { data: vehiclesFromDto(response.data), meta: response.meta };
    },
    async countVehicles(query) {
      const { data } = countsSchema.parse(
        await http.get(`${TRACKING_PATH}/vehicles/counts`, { params: params(query) }),
      );
      return countsFromDto(data);
    },
    async listVehicleIds(query) {
      return idsSchema.parse(
        await http.get(`${TRACKING_PATH}/vehicles/ids`, { params: params(query) }),
      ).data;
    },
    async getPlayback(vehicleId, range) {
      const response = await http.get(
        `${TRACKING_PATH}/vehicles/${encodeURIComponent(vehicleId)}/playback`,
        { params: { from: range.from, to: range.to } },
      );
      const points = parsePlaybackPoints(response);
      // The endpoint sends points only; the segments are worked out here.
      return {
        points,
        segments: derivePlaybackSegments(vehicleId, points),
        alerts: playbackAlertsOf(points),
      };
    },
    async getAddress({ latitude, longitude }) {
      const { data } = addressSchema.parse(
        await http.get(`${TRACKING_PATH}/location`, {
          params: { lat: latitude, lng: longitude },
        }),
      );
      return { en: addressText(data.addressEn), ar: addressText(data.addressAr) };
    },
  };
}
